/**
 * uiRegistry.tsx (JARVIS-PLATFORM-ORB Phase 1)
 *
 * The generative-UI component registry for the operator orb. orbi-operator-ask
 * may return a `ui` array of display blocks beside its prose answer; this module
 * is the ONLY way those blocks reach the screen.
 *
 * SECURITY MODEL (red-team finding 3): model output is never rendered as HTML.
 * Blocks arrive as JSON, are validated server-side against this same registry,
 * and are validated AGAIN here (defence in depth): unknown types and fields are
 * dropped, strings are clipped and de-dashed, caps are enforced. Everything
 * paints through React text nodes and token-driven CSS classes only.
 *
 * ACTION MODEL (rule 51): a button is a claim of capability, so the action
 * allowlist carries only wired, gated paths:
 *   navigate -> known sections via the host router (same path as "Take me there")
 *   call     -> the confirm-on-tap dialer -> orbi-operator-call (server allowlist)
 *   ask      -> feeds a follow-up prompt back into the ask loop
 *   download -> opens a SERVER-SIGNED storage URL only (see validDownloadUrl);
 *               the model can never author one, the ask fn appends the block
 *               after validation (Phase 6)
 *   resolve_fire -> drives the SAME onResolveFire path fire_mass_resolve uses
 *               (mass-resolve preview review, round 3 item 4); no params
 *               needed, and the native window.confirm() in that handler is
 *               still the one fire-confirm gate regardless of entry point
 *
 * The trust donut mirrors the lifecycle-panel donut spec (confidence 0..1 from
 * get-customer-insights / orbi_customer_insights). When no grounded confidence
 * exists it renders the HONEST pre-state ("Not enough history yet"), never a
 * fabricated 0 percent (red-team finding 2).
 *
 * Tokens only. No em-dashes. No "Orbi" strings in any copy this module renders
 * (rule 52).
 */

import React from 'react';
import { donutSpec, chipDisplay } from './generativeUi';
import { normalizeCardText } from './cardText';

export type UiActionKind = 'navigate' | 'call' | 'ask' | 'download' | 'resolve_fire';

export interface UiAction {
  label: string;
  action: UiActionKind;
  params: {
    section?: string;
    client_slug?: string;
    account_number?: string;
    name?: string;
    reason?: string;
    prompt?: string;
    url?: string;
  };
}

export interface UiBlock {
  // trust_score_donut is the procured catalog donut (a real SVG ring);
  // trust_donut is the legacy model-path alias, rendered through the SAME
  // real ring so there is no pie stand-in anywhere.
  type: 'subscriber_card' | 'trust_donut' | 'trust_score_donut' | 'metric_chips' | 'options' | 'table' | 'report_link' | 'unprocured';
  props: Record<string, any>;
  actions?: UiAction[];
}

/* Phase 6: a download action is accepted ONLY when its URL is a signed object
   URL on OUR project's storage. The ask backend is the only author of these
   (it uploads the server-composed report and signs the path); anything else,
   including a model-invented link, never renders. */
/* DROP-IN (Phase 3 binding 4): the signed-URL origin comes from config only.
   With no configured project URL nothing can validate, so no download action
   ever renders - fail closed, never against a hardcoded foreign project. */
const CONFIG_SUPABASE_URL: string =
  (((typeof window !== 'undefined' && (window as any).__ORBI_ENV__) || {}).SUPABASE_URL || '');
const STORAGE_ORIGIN: string = CONFIG_SUPABASE_URL ? CONFIG_SUPABASE_URL + '/storage/v1/object/sign/' : '';

function validDownloadUrl(u: unknown): string | null {
  // Empty origin (no configured project) must reject, never match-all.
  return (STORAGE_ORIGIN && typeof u === 'string' && u.startsWith(STORAGE_ORIGIN)) ? u : null;
}

const UI_BLOCK_CAP = 4;
const UI_ACTION_CAP = 2;
const NAV_SECTIONS = ['dashboard', 'customers', 'network', 'appointments', 'costs', 'discovery', 'tickets', 'billing', 'resolve', 'settings'];
const BADGE_VARIANTS = ['ok', 'warn', 'danger', 'muted'];

function deDash(s: string): string {
  return s.replace(/\s*[—–]\s*/g, ' - ');
}

function str(v: unknown, n: number): string | null {
  if (typeof v !== 'string') return null;
  const t = deDash(v).replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, n) : null;
}

function validActions(raw: unknown): UiAction[] {
  if (!Array.isArray(raw)) return [];
  const out: UiAction[] = [];
  for (const a of raw) {
    if (out.length >= UI_ACTION_CAP) break;
    if (!a || typeof a !== 'object') continue;
    const label = str((a as any).label, 40);
    const action = (a as any).action;
    const p = (a as any).params || {};
    if (!label) continue;
    if (action === 'navigate' && typeof p.section === 'string' && NAV_SECTIONS.includes(p.section)) {
      out.push({ label, action, params: { section: p.section, client_slug: typeof p.client_slug === 'string' ? p.client_slug : '' } });
    } else if (action === 'call' && str(p.account_number, 24)) {
      out.push({ label, action, params: { account_number: str(p.account_number, 24)!, name: str(p.name, 60) || undefined, reason: str(p.reason, 120) || undefined } });
    } else if (action === 'ask' && str(p.prompt, 200)) {
      out.push({ label, action, params: { prompt: str(p.prompt, 200)! } });
    } else if (action === 'download' && validDownloadUrl(p.url)) {
      out.push({ label, action, params: { url: validDownloadUrl(p.url)! } });
    } else if (action === 'resolve_fire') {
      out.push({ label, action, params: {} });
    }
  }
  return out;
}

/** Client-side registry validation. The server validated already; this repeats the
 *  gate so a compromised or stale response still cannot smuggle an unknown block,
 *  an unbounded string, or an unlisted action into the panel. */
/* CATALOGUE, NOT SILENCE (Phase 3 binding 3): the five renderer keys the
   jarvis_card_renderers catalogue registers but has NOT procured (blocked as
   Eri procurement pending the design system). A block naming one of these
   renders a greyed "not yet available" placeholder instead of vanishing -
   an unprocured capability the operator can see beats one that silently
   never appears. Keep in step with the catalogue's procured flags. */
export const UNPROCURED_RENDERERS = ['anomaly_chart', 'status_matrix', 'interaction_timeline', 'kpi_stat', 'breakdown_donut'];

export function validateUiBlocks(raw: unknown): UiBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: UiBlock[] = [];
  for (const b of raw) {
    if (out.length >= UI_BLOCK_CAP) break;
    if (!b || typeof b !== 'object') continue;
    const type = (b as any).type;
    const props = (b as any).props || {};
    const actions = validActions((b as any).actions);
    if (type === 'subscriber_card') {
      const heading = str(props.heading, 60);
      if (!heading) continue;
      const badges = Array.isArray(props.badges)
        ? props.badges.slice(0, 4)
            .map((x: any) => ({ text: str(x?.text, 24), variant: BADGE_VARIANTS.includes(x?.variant) ? x.variant : 'muted' }))
            .filter((x: any) => x.text)
        : [];
      out.push({ type, props: { heading, metric: str(props.metric, 40), description: str(normalizeCardText(props.description) ?? props.description, 160), badges, account_number: str(props.account_number, 24) }, actions });
    } else if (type === 'trust_donut' || type === 'trust_score_donut') {
      const c = typeof props.confidence === 'number' && isFinite(props.confidence) && props.confidence >= 0 && props.confidence <= 1 ? props.confidence : null;
      const interactions = typeof props.interactions === 'number' && isFinite(props.interactions) && props.interactions >= 0 ? Math.floor(props.interactions) : null;
      out.push({ type, props: { heading: str(props.heading, 60), confidence: c, interactions, last_contact: str(props.last_contact, 40) }, actions });
    } else if (type === 'metric_chips') {
      const chips = Array.isArray(props.chips)
        ? props.chips.slice(0, 6)
            .map((ch: any) => ({ label: str(ch?.label, 30), value: str(String(ch?.value ?? ''), 20), variant: BADGE_VARIANTS.includes(ch?.variant) ? ch.variant : 'muted' }))
            .filter((ch: any) => ch.label && ch.value)
        : [];
      if (!chips.length) continue;
      out.push({ type, props: { heading: str(props.heading, 60), chips }, actions });
    } else if (type === 'options') {
      if (!actions.length) continue;
      out.push({ type, props: { heading: str(props.heading, 80) }, actions });
    } else if (type === 'report_link') {
      // Only meaningful with a validated download action; a report_link with
      // nothing to download is an empty claim and never renders.
      const dl = actions.filter((a) => a.action === 'download');
      if (!dl.length) continue;
      out.push({ type, props: { heading: str(props.heading, 60) || 'Report', format: str(props.format, 12), note: str(props.note, 120) }, actions: dl.slice(0, 1) });
    } else if (type === 'table') {
      const columns = Array.isArray(props.columns) ? props.columns.slice(0, 4).map((c: any) => str(c, 24)).filter(Boolean) as string[] : [];
      const rows = Array.isArray(props.rows)
        ? (props.rows.slice(0, 6)
            .map((r: any) => (Array.isArray(r) ? r.slice(0, columns.length).map((c: any) => str(String(c ?? ''), 40) || '') : null))
            .filter(Boolean) as string[][])
        : [];
      if (!columns.length || !rows.length) continue;
      out.push({ type, props: { heading: str(props.heading, 60), columns, rows }, actions });
    } else if (type === 'unprocured' || UNPROCURED_RENDERERS.includes(type)) {
      // Registered-but-unprocured: render the honest placeholder, never drop.
      const key = type === 'unprocured' ? (str(props.renderer_key, 30) || 'this visual') : type;
      out.push({ type: 'unprocured', props: { renderer_key: key, heading: str(props.heading, 60) }, actions: [] });
    }
  }
  return out;
}

/* ── Renderers. Text nodes + token classes only; no dangerouslySetInnerHTML. ── */

const ActionRow: React.FC<{ actions?: UiAction[]; onAction: (a: UiAction) => void; pending?: boolean }> = ({ actions, onAction, pending }) => {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="orbi-ui-actions">
      {actions.map((a, i) => (
        <button
          key={i}
          type="button"
          className={`orbi-ui-action orbi-ui-action-${a.action}`}
          onClick={() => onAction(a)}
          disabled={pending}
          title={a.action === 'call' ? `${a.label} (confirm first; outbound is allowlist-gated)` : a.label}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
};

const SubscriberCard: React.FC<{ block: UiBlock; onAction: (a: UiAction) => void; pending?: boolean }> = ({ block, onAction, pending }) => {
  const p = block.props;
  return (
    <div className="orbi-ui-block orbi-ui-subscriber">
      <div className="orbi-ui-heading">{p.heading}</div>
      {p.metric && <div className="orbi-ui-metric">{p.metric}</div>}
      {p.description && <div className="orbi-ui-desc">{p.description}</div>}
      {p.badges && p.badges.length > 0 && (
        <div className="orbi-ui-badges">
          {p.badges.map((b: any, i: number) => (
            <span key={i} className={`orbi-ui-badge orbi-ui-badge-${b.variant}`}>{b.text}</span>
          ))}
        </div>
      )}
      <ActionRow actions={block.actions} onAction={onAction} pending={pending} />
    </div>
  );
};

/* The procured trust_score_donut: a REAL SVG ring (stroke-only arcs over a
   genuinely empty centre), not a conic-gradient pie hidden behind an opaque
   disc. The ring geometry comes from donutSpec (the same spec the test pins),
   so a pie regression fails the test before it reaches a screen. Confidence
   null renders the honest pre-state, never a fabricated 0 percent. */
const TrustDonut: React.FC<{ block: UiBlock; onAction: (a: UiAction) => void; pending?: boolean }> = ({ block, onAction, pending }) => {
  const p = block.props;
  const pct = p.confidence != null ? Math.round(p.confidence * 100) : null;
  return (
    <div className="orbi-ui-block orbi-ui-trust">
      <div className="orbi-ui-heading">{p.heading || 'Trust state'}</div>
      {pct != null ? (
        <div className="orbi-ui-trust-row">
          {(() => {
            const d = donutSpec(pct);
            return (
              <svg className="orbi-ui-donut" viewBox={d.viewBox} width={d.size} height={d.size} role="img" aria-label={`Trust ${d.pct} percent`}>
                {d.circles.map((c, i) => (
                  <circle
                    key={i}
                    cx={d.center}
                    cy={d.center}
                    r={d.radius}
                    fill={c.fill}
                    stroke={c.stroke}
                    strokeWidth={d.stroke}
                    strokeLinecap="round"
                    {...(c.dashArray ? { strokeDasharray: c.dashArray, transform: `rotate(-90 ${d.center} ${d.center})` } : {})}
                  />
                ))}
                <text className="orbi-ui-donut-v" x={d.center} y={d.center - 1} textAnchor="middle" dominantBaseline="central">{d.pct}%</text>
                <text className="orbi-ui-donut-l" x={d.center} y={d.center + 12} textAnchor="middle" dominantBaseline="central">trust</text>
              </svg>
            );
          })()}
          <div className="orbi-ui-trust-meta">
            {p.interactions != null && <span>{p.interactions} interaction{p.interactions === 1 ? '' : 's'}</span>}
            {p.last_contact && <span>Last contact {p.last_contact}</span>}
          </div>
        </div>
      ) : (
        <div className="orbi-ui-trust-empty">Not enough history yet. The score builds as conversations land.</div>
      )}
      <ActionRow actions={block.actions} onAction={onAction} pending={pending} />
    </div>
  );
};

/* A chip value like "696" or "$1,240" reduced to its number, or null when the
   value is not numeric. Drives the mini impact bars below. */
function chipNumber(v: string): number | null {
  const m = String(v).replace(/[,\s]/g, '').match(/-?\d+(?:\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return isFinite(n) && n >= 0 ? n : null;
}

const MetricChips: React.FC<{ block: UiBlock; onAction: (a: UiAction) => void; pending?: boolean }> = ({ block, onAction, pending }) => {
  const p = block.props;
  /* Mini impact bars (the Discovery bar pattern): each numeric chip gets a bar
     scaled to the largest value in the row. Pure presentation of the numbers
     already shown - nothing derived or invented. Only drawn when at least two
     chips are numeric, so a lone figure never gets a meaningless full bar. */
  const nums = p.chips.map((c: any) => chipNumber(c.value));
  const numericCount = nums.filter((n: number | null) => n != null).length;
  const max = Math.max(0, ...nums.filter((n: number | null): n is number => n != null));
  const showBars = numericCount >= 2 && max > 0;
  return (
    <div className="orbi-ui-block orbi-ui-metrics">
      {p.heading && <div className="orbi-ui-heading">{p.heading}</div>}
      <div className="orbi-ui-chips">
        {p.chips.map((c: any, i: number) => {
          // One fact per chip: a label that leads with its own value ("16
          // resolve dispatches", value "16") would render the number twice.
          const d = chipDisplay(c.label, c.value);
          return (
          <span key={i} className={`orbi-ui-chip orbi-ui-chip-${c.variant}`}>
            <span className="orbi-ui-chip-v">{d.value}</span>
            <span className="orbi-ui-chip-l">{d.label}</span>
            {showBars && nums[i] != null && (
              <span className="orbi-ui-chip-bar" aria-hidden="true">
                <span className="orbi-ui-chip-bar-fill" style={{ width: `${Math.max(3, Math.round((nums[i]! / max) * 100))}%` }} />
              </span>
            )}
          </span>
          );
        })}
      </div>
      <ActionRow actions={block.actions} onAction={onAction} pending={pending} />
    </div>
  );
};

const OptionsBlock: React.FC<{ block: UiBlock; onAction: (a: UiAction) => void; pending?: boolean }> = ({ block, onAction, pending }) => (
  <div className="orbi-ui-block orbi-ui-options">
    {block.props.heading && <div className="orbi-ui-heading">{block.props.heading}</div>}
    <ActionRow actions={block.actions} onAction={onAction} pending={pending} />
  </div>
);

const TableBlock: React.FC<{ block: UiBlock; onAction: (a: UiAction) => void; pending?: boolean }> = ({ block, onAction, pending }) => {
  const p = block.props;
  return (
    <div className="orbi-ui-block orbi-ui-table-wrap">
      {p.heading && <div className="orbi-ui-heading">{p.heading}</div>}
      <table className="orbi-ui-table">
        <thead>
          <tr>{p.columns.map((c: string, i: number) => <th key={i}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {p.rows.map((r: string[], i: number) => (
            <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
      <ActionRow actions={block.actions} onAction={onAction} pending={pending} />
    </div>
  );
};

/* A generated report: heading + format tag + the one download button. */
const ReportLink: React.FC<{ block: UiBlock; onAction: (a: UiAction) => void; pending?: boolean }> = ({ block, onAction, pending }) => {
  const p = block.props;
  return (
    <div className="orbi-ui-block orbi-ui-report">
      <div className="orbi-ui-report-head">
        <span className="orbi-ui-heading">{p.heading}</span>
        {p.format && <span className="orbi-ui-report-fmt">{String(p.format).toUpperCase()}</span>}
      </div>
      {p.note && <div className="orbi-ui-desc">{p.note}</div>}
      <ActionRow actions={block.actions} onAction={onAction} pending={pending} />
    </div>
  );
};

/* Phase 3 binding 3: a registered-but-unprocured catalogue renderer shows as a
   greyed, inert placeholder - the operator sees the capability exists and is
   not yet available, instead of nothing at all. Design tokens only (the
   orbi-ui-unprocured class); no colour lives here. */
const UnprocuredBlock: React.FC<{ block: UiBlock }> = ({ block }) => {
  const key = String(block.props.renderer_key || 'this visual').replace(/_/g, ' ');
  return (
    <div className="orbi-ui-block orbi-ui-unprocured" aria-disabled="true">
      {block.props.heading ? <div className="orbi-ui-heading">{block.props.heading}</div> : null}
      <div className="orbi-ui-desc">The {key} view is not yet available. It is registered in the catalogue and arrives with the design system.</div>
    </div>
  );
};

export const UiBlocks: React.FC<{ blocks?: UiBlock[]; onAction: (a: UiAction) => void; pending?: boolean }> = ({ blocks, onAction, pending }) => {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="orbi-ui-stack">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'subscriber_card': return <SubscriberCard key={i} block={b} onAction={onAction} pending={pending} />;
          case 'trust_donut':
          case 'trust_score_donut': return <TrustDonut key={i} block={b} onAction={onAction} pending={pending} />;
          case 'metric_chips':    return <MetricChips key={i} block={b} onAction={onAction} pending={pending} />;
          case 'options':         return <OptionsBlock key={i} block={b} onAction={onAction} pending={pending} />;
          case 'table':           return <TableBlock key={i} block={b} onAction={onAction} pending={pending} />;
          case 'report_link':     return <ReportLink key={i} block={b} onAction={onAction} pending={pending} />;
          case 'unprocured':      return <UnprocuredBlock key={i} block={b} />;
          default:                return null; /* unknown types never paint */
        }
      })}
    </div>
  );
};
